import { useRouter } from 'next/router';
import { memo, useCallback, useMemo } from 'react';

import { useImportManagerSession } from '~/components/Import/useImportManagerSession';
import { useImportPaneDismissLocalTasks } from '~/components/Import/useImportPaneDismissLocalTasks';
import { MainSideNavOverlayTray } from '~/components/v2/components/workspace/main-side-nav/MainSideNavOverlayTray';
import { MainSideNavOverlayTrayHeader } from '~/components/v2/components/workspace/main-side-nav/MainSideNavOverlayTrayHeader';
import { useMainSideNavOverlay } from '~/components/v2/components/workspace/main-side-nav/useMainSideNavOverlay';
import { buildWorkspaceImportRows } from '~/components/v2/components/workspace/workspace-imports/buildWorkspaceImportRows';
import {
  getWorkspaceImportsCounts,
  getWorkspaceImportsHeader,
} from '~/components/v2/components/workspace/workspace-imports/getWorkspaceImportsHeader';
import { WorkspaceImportsCloseButton } from '~/components/v2/components/workspace/workspace-imports/WorkspaceImportsCloseButton';
import { WorkspaceImportsResumeAllButton } from '~/components/v2/components/workspace/workspace-imports/WorkspaceImportsResumeAllButton';
import { WorkspaceImportsVirtualizedList } from '~/components/v2/components/workspace/workspace-imports/WorkspaceImportsVirtualizedList';
import { isSubscriptionCheckoutRoute } from '~/constants/routes';
import { WORKSPACE_IMPORTS_TRAY_ID } from '~/constants/workspaceActivity';
import { useStoragePlanRestriction } from '~/hooks/useStoragePlanRestriction';
import { useCurrentWorkspacePermissionsContext } from '~/providers/CurrentWorkspacePermissionsProvider';
import { useSubscriptionContext } from '~/providers/SubscriptionProvider';
import { useStorage } from '~/swr-hooks/subscriptions/useStorage';
import { canManageSubscription } from '~/utils/permissions/workspacePermissions';

export const WorkspaceImportsTray = memo(() => {
  const router = useRouter();
  const { isOpen, onOpenChange } = useMainSideNavOverlay({ id: 'imports' });
  const { data: permissions } = useCurrentWorkspacePermissionsContext();
  const { data: subscription } = useSubscriptionContext();
  const { data: storage } = useStorage();
  const { isStoragePlanRestricted } = useStoragePlanRestriction();
  const { hasStoppedImports, importerTasks, imports, stoppedImports } = useImportManagerSession();

  const disableResume =
    (subscription?.plan === 'basic' || subscription?.pricingModel === 'credit') && isStoragePlanRestricted;
  const rows = useMemo(
    () =>
      buildWorkspaceImportRows({
        disableResume,
        localTasks: importerTasks,
        remoteImports: imports,
      }),
    [disableResume, importerTasks, imports],
  );
  const counts = useMemo(() => getWorkspaceImportsCounts(rows), [rows]);
  const { dismissCompletedAndErroredLocalImportTasks } = useImportPaneDismissLocalTasks({
    localTasks: importerTasks,
  });
  const isUpgradingPlan = isSubscriptionCheckoutRoute(router.pathname);
  const { banner, description, title } = useMemo(
    () =>
      getWorkspaceImportsHeader({
        canUpgradePlan: canManageSubscription(permissions),
        counts,
        isCreditModel: subscription?.isCreditModel ?? false,
        isInFreePlan: subscription?.isInFreePlan ?? false,
        isStoragePlanRestricted,
        isUpgradeSuccess: isUpgradingPlan && subscription?.plan === router.query.planId,
        isUpgradingPlan,
        maxGb: storage?.maxGb,
        plan: subscription?.plan,
      }),
    [
      counts,
      isStoragePlanRestricted,
      isUpgradingPlan,
      permissions,
      router.query.planId,
      storage?.maxGb,
      subscription?.isCreditModel,
      subscription?.isInFreePlan,
      subscription?.plan,
    ],
  );
  const showResumeAll = hasStoppedImports && !subscription?.isInFreePlan && subscription?.pricingModel !== 'credit';
  const stoppedImportIds = useMemo(() => stoppedImports.map((item) => item.id), [stoppedImports]);

  const handleOpenChange = useCallback(
    (nextOpen: boolean) => {
      if (!nextOpen) {
        dismissCompletedAndErroredLocalImportTasks();
      }
      onOpenChange(nextOpen);
    },
    [dismissCompletedAndErroredLocalImportTasks, onOpenChange],
  );

  const handleCloseImportsPane = useCallback(() => {
    dismissCompletedAndErroredLocalImportTasks();
    onOpenChange(false);
  }, [dismissCompletedAndErroredLocalImportTasks, onOpenChange]);

  return (
    <MainSideNavOverlayTray
      contentId={WORKSPACE_IMPORTS_TRAY_ID}
      contentTestId="WORKSPACE_IMPORTS_PANEL"
      onOpenChange={handleOpenChange}
      open={isOpen}
      overlayTestId="WORKSPACE_IMPORTS_OVERLAY"
      placement="bottom"
      shrinkIntoTrigger
    >
      <MainSideNavOverlayTrayHeader
        description={description}
        rightSuffix={
          showResumeAll ? (
            <div className="flex shrink-0 items-center gap-1">
              <WorkspaceImportsResumeAllButton stoppedImportIds={stoppedImportIds} />
              <WorkspaceImportsCloseButton onCloseImportsPane={handleCloseImportsPane} />
            </div>
          ) : (
            <WorkspaceImportsCloseButton onCloseImportsPane={handleCloseImportsPane} />
          )
        }
        title={title}
      />
      <WorkspaceImportsVirtualizedList banner={banner} rows={rows} />
    </MainSideNavOverlayTray>
  );
});

WorkspaceImportsTray.displayName = 'WorkspaceImportsTray';
